import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { AumicoAbcpConfig, AumicoAbcpConfigService } from './abcp-data.config';
import { AccountLedgerEffects } from './effects/account-ledger.effects';
import { ClosingAdjustmentEffects } from './effects/closing-adjustment.effects';
import { ClosingDocumentEffects } from './effects/closing-document.effects';
import { ClosingNoteEffects } from './effects/closing-note.effects';
import { ClosingSetupEffects } from './effects/closing-setup.effects';
import { ProjectAccountsEffects } from './effects/project-accounts.effects';
import { AccountLedgerProvider } from './provider/account-ledger.provider';
import { ClosingNoteProvider } from './provider/closing-note.provider';
import { ClosingSetupProvider } from './provider/closing-setup.provider';
import { ProjectAccountsProvider } from './provider/project-accounts.provider';
import { ACCOUNT_LEDGER_FEATURE_KEY, accountLedgerReducer } from './reducer/account-ledger.reducer';
import { CLOSING_ADJUSTMENT_FEATURE_KEY } from './reducer/closing-adjustment.feature-key';
import { closingAdjustmentReducer } from './reducer/closing-adjustment.reducer';
import { CLOSING_DOCUMENT_FEATURE_KEY } from './reducer/closing-document.feature-key';
import { closingDocumentReducer } from './reducer/closing-document.reducer';
import { CLOSING_NOTE_FEATURE_KEY, closingNoteReducer } from './reducer/closing-note.reducer';
import { CLOSING_SETUP_FEATURE_KEY, closingSetupReducer } from './reducer/closing-setup.reducer';
import { PROJECT_ACCOUNTS_FEATURE_KEY, projectAccountsReducer } from './reducer/project-accounts.reducer';
import { AccountLedgerService } from './services/account-ledger.service';
import { ClosingActivityService } from './services/closing-activity.service';
import { ClosingSettingsService } from './services/closing-settings.service';
import { ClosingAdjustmentService } from './services/closing-adjustment.service';
import { ClosingDocumentService } from './services/closing-document.service';
import { ClosingNoteService } from './services/closing-note.service';
import { ClosingSetupService } from './services/closing-setup.service';
import { ProjectAccountsService } from './services/project-accounts.service';

/**
 * The ABCP DATA layer: the NgRx slices and HTTP services behind the closing.
 *
 * Deliberately SEPARATE from `AbcpUiModule`. That module holds the pure views, and the pure-view
 * convention (`conventions.md` §ABCP-FE) is that they inject no Store and do no HTTP. Putting a store
 * feature and an HTTP service into it would make every consumer of a dumb component drag a slice
 * along, and would blur the one rule those components are built around.
 *
 * FOUR slices live here, one per surface — the documents of a closing (REV-21376), its accounts
 * (REV-21787), its Setup-Wizard (REV-21354) and its correction bookings (REV-21375). They are
 * separate features and share no state; only the module and the API-gateway config are common,
 * which is why one module registers all four instead of each growing its own.
 *
 * ⚠️ On 16.09.2026 this list carried a note (SRR R4-6) that the named „Setup-Wizard (REV-21354)“
 * slice did not exist — true for this branch at the time, where the third `forFeature` was
 * `CLOSING_ADJUSTMENT_FEATURE_KEY`. The merge of `development` landed the slice for real, so the
 * note has expired and is retired here rather than carried forward as a second wrong statement.
 */
@NgModule({
    imports: [
        CommonModule,
        StoreModule.forFeature(CLOSING_DOCUMENT_FEATURE_KEY, closingDocumentReducer),
        StoreModule.forFeature(PROJECT_ACCOUNTS_FEATURE_KEY, projectAccountsReducer),
        StoreModule.forFeature(CLOSING_SETUP_FEATURE_KEY, closingSetupReducer),
        // REV-21375 — the correction bookings of a closing (ADR-0015). A THIRD feature beside the
        // documents and the accounts: same module, separate slice. They share no state.
        StoreModule.forFeature(CLOSING_ADJUSTMENT_FEATURE_KEY, closingAdjustmentReducer),
        // REV-21374 — Kommentare und Pendenzen (ADR-0019). EIN Slice fuer BEIDE Register und
        // den Konto-Bereich: es ist ein Aggregat mit `kind`-Diskriminator (E1), und zwei Slices
        // haetten zwei Wahrheiten ueber denselben Eintrag.
        StoreModule.forFeature(CLOSING_NOTE_FEATURE_KEY, closingNoteReducer),
        // The Kontenblatt of one account: its own slice, because its pages append.
        StoreModule.forFeature(ACCOUNT_LEDGER_FEATURE_KEY, accountLedgerReducer),
        EffectsModule.forFeature([
            ClosingDocumentEffects,
            ProjectAccountsEffects,
            ClosingSetupEffects,
            ClosingAdjustmentEffects,
            ClosingNoteEffects,
            AccountLedgerEffects,
        ]),
    ],
})
export class AbcpDataModule {
    public static forRoot(config: AumicoAbcpConfig): ModuleWithProviders<AbcpDataModule> {
        return {
            ngModule: AbcpDataModule,
            providers: [
                // REV-21378 — the HUMAN half of the activity log; the agent half comes from
                // `AiAgentHttpService` and the client merges the two (ADR-0018 E4).
                ClosingActivityService,
                ClosingDocumentService,
                ClosingAdjustmentService,
                ProjectAccountsService,
                ProjectAccountsProvider,
                ClosingSetupService,
                ClosingSetupProvider,
                // REV-21374 — EINE Tuer fuer beide Register und den Konto-Bereich (E6).
                ClosingNoteService,
                ClosingNoteProvider,
                AccountLedgerService,
                AccountLedgerProvider,
                ClosingSettingsService,
                { provide: AumicoAbcpConfigService, useValue: config },
            ],
        };
    }
}
